"use client";

import React, { useState } from "react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { RangeInput } from "@/components/ui/range-input";
const comparisonData = [{
  item: "Förare (Lön & Sociala avgifter)",
  traditional: "~45 000 kr",
  robotaxi: "0 kr"
}, {
  item: "Drivmedel / Energi",
  traditional: "~8 000 kr",
  robotaxi: "~2 500 kr"
}, {
  item: "Försäkring",
  traditional: "~3 500 kr",
  robotaxi: "~1 500 kr (Gruppavtal)"
}, {
  item: "Underhåll & Service",
  traditional: "~4 000 kr",
  robotaxi: "~3 000 kr"
}, {
  item: "Plattformsavgift / Växel",
  traditional: "~10 000 kr",
  robotaxi: "~5 000 kr"
}, {
  item: "Total uppskattad månadskostnad",
  traditional: "~70 500 kr",
  robotaxi: "~12 000 kr"
}];
export default function CalculateYourProfit() {
  const [vehicles, setVehicles] = useState<number>(1);
  const [hoursPerDay, setHoursPerDay] = useState<number>(12);
  const revenuePerHour = 250;
  const costPerHour = 40;
  const daysPerMonth = 30;
  const monthlyRevenue = vehicles * hoursPerDay * revenuePerHour * daysPerMonth;
  const monthlyCost = vehicles * hoursPerDay * costPerHour * daysPerMonth;
  const monthlyProfit = monthlyRevenue - monthlyCost;
  const maxBarValue = monthlyRevenue > 0 ? monthlyRevenue : 1;
  const costWidth = monthlyCost / maxBarValue * 100;
  const profitWidth = monthlyProfit / maxBarValue * 100;
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat("sv-SE", {
      style: "currency",
      currency: "SEK",
      maximumFractionDigits: 0
    }).format(value);
  };
  return <section id="kalkylera-din-vinst" className="dark bg-background text-foreground py-24 px-6 md:px-12 lg:px-24">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-primary mb-4">
            Kalkylera din vinst
          </h2>
          <p className="font-sans text-muted-foreground max-w-2xl mx-auto text-lg">
            Jämför de löpande kostnaderna mellan en traditionell taxiverksamhet
            och en autonom robotaxi-flotta. Använd kalkylatorn för att estimera
            din potentiella avkastning.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          {}
          <div className="bg-card border border-border rounded-lg p-6 shadow-lg">
            <h3 className="font-serif text-xl font-bold mb-6 text-card-foreground">
              Kostnadsjämförelse (per fordon/månad)
            </h3>
            <Table>
              <TableHeader>
                <TableRow className="border-border hover:bg-transparent">
                  <TableHead className="text-muted-foreground">
                    Kostnadspost
                  </TableHead>
                  <TableHead className="text-muted-foreground">
                    Traditionell Taxi
                  </TableHead>
                  <TableHead className="text-primary font-bold">
                    Robotaxi
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {comparisonData.map((row, i) => <TableRow key={i} data-index={i} className="border-border hover:bg-muted/20 transition-colors">
                    <TableCell className="font-medium text-card-foreground py-4">
                      {row.item}
                    </TableCell>
                    <TableCell className="text-muted-foreground py-4">
                      {row.traditional}
                    </TableCell>
                    <TableCell className="text-primary font-medium py-4">
                      {row.robotaxi}
                    </TableCell>
                  </TableRow>)}
              </TableBody>
            </Table>
            <p className="text-xs text-muted-foreground mt-4 italic">
              * Siffrorna är uppskattningar baserade på branschgenomsnitt och
              kan variera beroende på fordonstyp och lokala avtal.
            </p>
          </div>

          {}
          <div className="bg-card border border-border rounded-lg p-6 shadow-lg flex flex-col h-full">
            <h3 className="font-serif text-xl font-bold mb-6 text-card-foreground">
              Interaktiv Vinstkalkylator
            </h3>

            <div className="space-y-8 flex-grow">
              {}
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <label className="font-sans text-sm font-medium text-card-foreground">
                    Antal fordon i flottan
                  </label>
                  <span className="font-mono text-lg font-bold text-primary bg-primary/10 px-3 py-1 rounded-md">
                    {vehicles} st
                  </span>
                </div>
                <RangeInput min={1} max={50} step={1} value={vehicles} onValueChange={setVehicles} className="slider-thumb:bg-primary" />
              </div>

              {}
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <label className="font-sans text-sm font-medium text-card-foreground">
                    Aktiva timmar per dygn
                  </label>
                  <span className="font-mono text-lg font-bold text-primary bg-primary/10 px-3 py-1 rounded-md">
                    {hoursPerDay} h
                  </span>
                </div>
                <RangeInput min={4} max={24} step={1} value={hoursPerDay} onValueChange={setHoursPerDay} className="slider-thumb:bg-primary" />
              </div>

              <div className="pt-6 border-t border-border space-y-6">
                {}
                <div>
                  <div className="flex justify-between text-sm mb-2">
                    <span className="text-muted-foreground">
                      Estimerad intäkt / mån
                    </span>
                    <span className="font-mono text-card-foreground">
                      {formatCurrency(monthlyRevenue)}
                    </span>
                  </div>
                  <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
                    <div className="h-full bg-muted-foreground/40 w-full transition-all duration-500"></div>
                  </div>
                </div>

                {}
                <div>
                  <div className="flex justify-between text-sm mb-2">
                    <span className="text-muted-foreground">
                      Driftskostnader / mån
                    </span>
                    <span className="font-mono text-card-foreground">
                      {formatCurrency(monthlyCost)}
                    </span>
                  </div>
                  <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
                    <div className="h-full bg-muted-foreground transition-all duration-500" style={{
                    width: `${costWidth}%`
                  }}></div>
                  </div>
                </div>

                {}
                <div className="bg-background/50 p-4 rounded-md border border-border/50">
                  <div className="flex justify-between text-base mb-3 font-bold">
                    <span className="text-card-foreground">
                      Estimerad Vinst / mån
                    </span>
                    <span className="font-mono text-primary text-xl">
                      {formatCurrency(monthlyProfit)}
                    </span>
                  </div>
                  <div className="w-full h-4 bg-muted rounded-full overflow-hidden shadow-inner">
                    <div className="h-full bg-primary transition-all duration-500 relative" style={{
                    width: `${profitWidth}%`
                  }}>
                      <div className="absolute inset-0 bg-white/20 w-full h-full animate-pulse"></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}